// Recipe-level technique validator orchestrator. Runs every
// applicable sub-validator (baking, meat-doneness, frying-heat,
// pasta-water, parallel-steps), applies their auto-fixes
// silently, and surfaces a single "✓ TECHNIQUE VALIDATED" badge
// to the user once the result is clean.
//
// Sub-validators each export:
//   - `appliesTo(recipe)` boolean predicate
//   - `validate(recipe, ctx)` returning { ok, issues, warnings }
//   - optional `autoFix(recipe, findings)` returning { recipe, changes }
//
// The orchestrator runs validate → autoFix → re-validate for each
// applicable sub-validator. Changes accumulate across sub-validators
// so the final recipe has every safe correction applied. Residual
// issues / warnings are merged into one diagnostics block; the
// existing AIRecipe band reads this without rework.
//
// Sister memory: feedback_validators_over_prompts.md — auto-fixes
// silent, residual findings surface only when the user has a real
// decision to make.

import { isBakingRecipe, validateBakingRecipe } from "./bakingRecipeValidator";
import { autoFixBakingRecipe } from "./autoFixBakingRecipe";
import { findStepParallelismIssues } from "./parallelStepValidator";
import {
  isMeatRecipe, validateMeatDoneness, autoFixMeatDoneness,
} from "./validators/meatDoneness";
import {
  isFryingRecipe, validateFryingHeat, autoFixFryingHeat,
} from "./validators/fryingHeat";
import {
  isPastaRecipe, validatePastaWater, autoFixPastaWater,
} from "./validators/pastaWater";

// Sub-validator registry. Each entry is { kind, appliesTo,
// validate, autoFix }. Adding a new technique check = adding a
// row here and an entry under src/lib/validators/.
const SUB_VALIDATORS = [
  {
    kind: "baking",
    appliesTo: isBakingRecipe,
    validate: (recipe, ctx) => validateBakingRecipe(recipe, ctx),
    autoFix:  (recipe, findings) => autoFixBakingRecipe(recipe, findings),
  },
  {
    kind: "meat",
    appliesTo: isMeatRecipe,
    validate: (recipe) => validateMeatDoneness(recipe),
    autoFix:  (recipe, findings) => autoFixMeatDoneness(recipe, findings),
  },
  {
    kind: "frying",
    appliesTo: isFryingRecipe,
    validate: (recipe) => validateFryingHeat(recipe),
    autoFix:  (recipe, findings) => autoFixFryingHeat(recipe, findings),
  },
  {
    kind: "pasta",
    appliesTo: isPastaRecipe,
    validate: (recipe) => validatePastaWater(recipe),
    autoFix:  (recipe, findings) => autoFixPastaWater(recipe, findings),
  },
];

/**
 * Run every applicable sub-validator with its auto-fix and a
 * re-validation pass. Returns { recipe, diagnostics } where:
 *   - recipe is the (possibly-corrected) recipe
 *   - diagnostics:
 *       ok: bool — true if no residual blockers
 *       applicable: string[] — sub-validator kinds that ran
 *       autoFixed: { kind, summary }[] — every silent correction
 *       issues / warnings: residual findings (post-fix)
 *       ratios?, type?, hydrationPct? — pass-through from baking
 *         when applicable, for backward compat with existing UI
 *
 * Parallel-step warnings ride on the warnings array under
 * source="parallelism". Never auto-fixed (the fix is generation-
 * side: prompt Claude to split into two steps).
 */
export function runRecipeTechniqueChecks(recipe, ctx = {}) {
  if (!recipe) return { recipe, diagnostics: null };

  const applicable = SUB_VALIDATORS.filter((v) => {
    try { return v.appliesTo(recipe); }
    catch (e) { console.warn("[recipeValidator] appliesTo threw for", v.kind, e); return false; }
  });

  if (applicable.length === 0) {
    // Recipe didn't trigger any technique check — return null
    // diagnostics. AIRecipe's render skips the band entirely.
    return { recipe, diagnostics: null };
  }

  let working = recipe;
  const autoFixed = [];

  // Per sub-validator: validate → if findings, autoFix → carry
  // working forward. Same order as SUB_VALIDATORS so structural
  // baking changes happen before meat / frying / pasta cue
  // appends (they're step-prose level, won't conflict).
  for (const v of applicable) {
    let findings;
    try { findings = v.validate(working, ctx); }
    catch (e) {
      console.warn("[recipeValidator] validate threw for", v.kind, e);
      continue;
    }
    if (!findings) continue;
    const hasAny = (findings.issues?.length || 0) + (findings.warnings?.length || 0);
    if (!hasAny || !v.autoFix) continue;
    try {
      const out = v.autoFix(working, findings);
      if (out?.changes?.length) {
        working = out.recipe;
        autoFixed.push(...out.changes.map((c) => ({ ...c, source: v.kind })));
      }
    } catch (e) {
      console.warn("[recipeValidator] autoFix threw for", v.kind, e);
    }
  }

  // Final pass on the post-fix recipe. Whatever remains in
  // issues/warnings is what the user actually needs to see.
  const finalIssues = [];
  const finalWarnings = [];
  let bakingExtras = null;
  for (const v of applicable) {
    let result;
    try { result = v.validate(working, ctx); }
    catch (e) {
      console.warn("[recipeValidator] final validate threw for", v.kind, e);
      continue;
    }
    if (!result) continue;
    for (const i of result.issues || []) finalIssues.push({ ...i, source: v.kind });
    for (const w of result.warnings || []) finalWarnings.push({ ...w, source: v.kind });
    if (v.kind === "baking") {
      bakingExtras = {
        type: result.type ?? null,
        hydrationPct: result.hydrationPct ?? null,
        ratios: result.ratios ?? null,
      };
    }
  }

  // Parallel-step warnings (always run, no auto-fix).
  const parallel = findStepParallelismIssues(working);
  for (const w of parallel.warnings || []) finalWarnings.push({ ...w, source: "parallelism" });

  const diagnostics = {
    ok: finalIssues.length === 0,
    applicable: applicable.map((v) => v.kind),
    issues: finalIssues,
    warnings: finalWarnings,
    autoFixed,
    // Backward-compat extras for the existing band's render path —
    // it reads `ratios` / `type` / `hydrationPct` for the baker's-
    // percentage snapshot. Null when the recipe isn't baking.
    ...(bakingExtras || {}),
  };

  if (autoFixed.length > 0) {
    console.info("[recipeValidator] auto-fixed", autoFixed.length, "issues silently:", autoFixed);
  }

  return { recipe: working, diagnostics };
}
